import { ReactNode } from "react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

interface SectionProps {
  children: ReactNode;
  id?: string;
  /** Tone of the section background */
  tone?: "default" | "alt" | "primary";
  className?: string;
  animate?: boolean;
}

export function Section({ children, id, tone = "default", className = "", animate = true }: SectionProps) {
  const animation = useScrollAnimation({ threshold: 0.08 });

  const style =
    tone === "alt"
      ? { backgroundColor: "var(--gradient-section-alt)" }
      : undefined;

  return (
    <section
      id={id}
      ref={animate ? (animation.ref as React.RefObject<HTMLElement>) : undefined}
      style={style}
      className={`scroll-mt-20 py-14 sm:py-24 ${
        tone === "primary" ? "bg-primary text-primary-foreground" : ""
      } ${
        animate
          ? `transition-[opacity,transform] duration-700 motion-reduce:transition-none ${
              animation.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
            }`
          : ""
      } ${className}`}
    >
      <div className="container mx-auto px-4">{children}</div>
    </section>
  );
}

interface SectionHeadingProps {
  eyebrow?: string;
  title: ReactNode;
  description?: ReactNode;
  align?: "center" | "left";
  className?: string;
  as?: "h1" | "h2";
}

export function SectionHeading({
  eyebrow,
  title,
  description,
  align = "center",
  className = "",
  as = "h2",
}: SectionHeadingProps) {
  const Heading = as;
  return (
    <div
      className={`${align === "center" ? "text-center mx-auto max-w-2xl" : "max-w-2xl"} mb-10 sm:mb-14 ${className}`}
    >
      {eyebrow && (
        <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.14em] text-primary mb-3">
          {eyebrow}
        </p>
      )}
      <Heading className="text-2xl sm:text-4xl md:text-[2.75rem] md:leading-[1.1] font-extrabold tracking-tight">
        {title}
      </Heading>
      {description && (
        <p className="mt-4 text-sm sm:text-lg text-muted-foreground leading-relaxed">{description}</p>
      )}
    </div>
  );
}
